import { BookOpen, PlayCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getCategoryBadgeClass, getCategoryLabel, normalizeCategoryList } from "@/lib/category-config";
import type { Series } from "@/types/series";

interface SeriesCardProps {
  series: Series;
  onClick?: () => void;
}

const jlptColor: Record<string, string> = {
  N5: "bg-emerald-500/15 text-emerald-700",
  N4: "bg-sky-500/15 text-sky-700",
  N3: "bg-amber-500/15 text-amber-700",
  N2: "bg-orange-500/15 text-orange-700",
  N1: "bg-rose-500/15 text-rose-700",
};

export function SeriesCard({ series, onClick }: SeriesCardProps) {
  if (!series) return null;

  const levels = Array.isArray(series.jlpt_levels) ? series.jlpt_levels : [];
  const categories = normalizeCategoryList(Array.isArray(series.categories) ? series.categories : []);

  return (
    <div
      className={cn(
        "group flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-border/50 bg-card transition-all duration-200",
        "hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-lg",
      )}
      onClick={onClick}
    >
      <div className="relative w-full overflow-hidden bg-muted aspect-[16/9]">
        {series.cover_url ? (
          <img
            src={series.cover_url}
            alt={series.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <BookOpen className="h-8 w-8 opacity-20" />
          </div>
        )}

        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <PlayCircle className="h-10 w-10 fill-white/20 text-white" />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <h3 className="line-clamp-2 text-sm font-bold leading-tight text-card-foreground transition-colors group-hover:text-primary sm:text-[0.95rem]">
          {series.title}
        </h3>

        <div className="flex flex-col gap-1.5 text-xs">
          <div className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0 text-muted-foreground">等级:</span>
            <div className="flex flex-wrap gap-1">
              {levels.length > 0 ? (
                levels.map((level) => (
                  <Badge
                    key={level}
                    className={cn(
                      "h-5 rounded-sm border-0 px-1.5 py-0 text-[10px] font-bold",
                      jlptColor[level] || "bg-muted text-muted-foreground",
                    )}
                  >
                    {level}
                  </Badge>
                ))
              ) : (
                <span className="text-muted-foreground/70">无</span>
              )}
            </div>
          </div>

          <div className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0 text-muted-foreground">分类:</span>
            <div className="flex flex-wrap gap-1">
              {categories.length > 0 ? (
                categories.map((category) => (
                  <Badge
                    key={category}
                    className={cn(
                      "h-5 rounded-sm border-0 px-1.5 py-0 text-[10px] font-medium",
                      getCategoryBadgeClass(category),
                    )}
                  >
                    {getCategoryLabel(category, true)}
                  </Badge>
                ))
              ) : (
                <span className="text-muted-foreground/70">无</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="shrink-0 text-muted-foreground">类型:</span>
            <span className="font-medium text-foreground/80">视频</span>
          </div>
        </div>
      </div>
    </div>
  );
}
